<template>
  <div class="flex flex-col">
    <AppModal v-model="showBlockedDetail" title="Details" size="lg" hide-footer @close="reasonRevealed = false">
      <div v-if="selectedBlocked" class="blocked-detail">
        <p v-for="entry in blockedDetailEntries" :key="entry.key" class="detail-row">
          <span class="detail-label">{{ entry.label }}:</span>
          <template v-if="entry.key === 'reason'">
            <span v-if="!reasonRevealed" class="detail-value-wrap inline-flex items-center gap-2">
              <span class="text-gray-500 tracking-widest">••••••••</span>
              <button
                type="button"
                class="text-gray-500 hover:text-primary"
                title="Show reason"
                @click="reasonRevealed = true"
              >
                <i class="fa-solid fa-eye"></i>
              </button>
            </span>
            <span v-else class="detail-value-wrap inline-flex items-start gap-2">
              <span>{{ entry.value }}</span>
              <button
                type="button"
                class="text-gray-500 hover:text-primary shrink-0"
                title="Hide reason"
                @click="reasonRevealed = false"
              >
                <i class="fa-solid fa-eye-slash"></i>
              </button>
            </span>
          </template>
          <span v-else class="detail-value-wrap">{{ entry.value }}</span>
        </p>
      </div>
    </AppModal>

    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Requests blocked by the API gateway, including method, path, and client IP.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadBlockedRequests">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
      </div>
    </div>

    <div v-if="loading && blockedRequests.length === 0" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
      <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
    </div>

    <div class="bg-white rounded border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-16">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Time</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Method</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Path</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">IP</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-28">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in paginatedItems"
              :key="blockedRowKey(row, idx)"
              class="hover:bg-gray-50 transition"
            >
              <td class="py-3 px-4 border-b border-gray-200">{{ rowNumber(idx) }}</td>
              <td class="py-3 px-4 border-b border-gray-200 whitespace-nowrap">{{ formatBlockedTime(row) }}</td>
              <td class="py-3 px-4 border-b border-gray-200">{{ rowField(row, "method") }}</td>
              <td
                class="py-3 px-4 border-b border-gray-200 max-w-[14rem] truncate"
                :title="rowField(row, 'path')"
              >
                {{ truncatePath(rowField(row, "path")) }}
              </td>
              <td class="py-3 px-4 border-b border-gray-200 whitespace-nowrap">{{ rowField(row, "ip") }}</td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                <button type="button" class="btn-sm-primary" @click="viewBlockedDetail(row)">
                  <i class="fa-solid fa-eye"></i>
                  <span>View</span>
                </button>
              </td>
            </tr>
            <tr v-if="blockedRequests.length === 0 && !loading">
              <td colspan="6" class="py-8 px-4 text-center text-gray-500">No results found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <GatewayTablePagination
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-elements="totalElements"
      :page-size="pageSize"
      :range-start="rangeStart"
      :range-end="rangeEnd"
      @previous="previous"
      @next="next"
      @update:page-size="onPageSizeUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import GatewayTablePagination from "./GatewayTablePagination.vue";
import { extractErrorMessage, fetchBlockedRequests } from "./gatewayApi";
import { useClientPagination } from "./useClientPagination";
import { useToast, useUtilities } from "@/composables";

const { errorToast } = useToast();
const { dateFormat } = useUtilities();

const PATH_DISPLAY_MAX = 50;

const truncatePath = (value: string) => {
  if (!value || value === "—") return value;
  if (value.length <= PATH_DISPLAY_MAX) return value;
  return value.substring(0, PATH_DISPLAY_MAX) + "...";
};

const loading = ref(false);
const blockedRequests = ref<Record<string, unknown>[]>([]);
const selectedBlocked = ref<Record<string, unknown> | null>(null);
const showBlockedDetail = ref(false);
const reasonRevealed = ref(false);

const {
  currentPage,
  pageSize,
  totalElements,
  totalPages,
  paginatedItems,
  rangeStart,
  rangeEnd,
  previous,
  next,
  resetPage,
} = useClientPagination(blockedRequests, 25);

const onPageSizeUpdate = (size: number) => {
  pageSize.value = size;
  resetPage();
};

const blockedDetailEntries = computed(() => {
  if (!selectedBlocked.value || typeof selectedBlocked.value !== "object") return [];
  const row = selectedBlocked.value;
  const labels: Record<string, string> = {
    id: "ID",
    method: "Method",
    path: "Path",
    reason: "Reason",
    clientIp: "Client IP",
    userAgent: "User agent",
    timestampEpochMillis: "Timestamp",
    ttlSeconds: "TTL (seconds)",
    headers: "Headers",
  };
  return Object.keys(row).map((key) => {
    let value = row[key];
    if (value == null || value === "") {
      value = "—";
    } else if (typeof value === "object") {
      try {
        value = JSON.stringify(value, null, 2);
      } catch {
        value = String(value);
      }
    } else {
      value = String(value);
    }
    return { key, label: labels[key] || key, value: value as string };
  });
});

const loadBlockedRequests = async () => {
  loading.value = true;
  try {
    blockedRequests.value = await fetchBlockedRequests();
    resetPage();
  } catch (error: unknown) {
    blockedRequests.value = [];
    resetPage();
    errorToast("Error", extractErrorMessage(error, "Could not load blocked requests"));
  } finally {
    loading.value = false;
  }
};

const rowField = (row: Record<string, unknown> | null, key: string) => {
  if (!row || typeof row !== "object") return "—";
  if (row[key] != null && row[key] !== "") return String(row[key]);
  if (key === "ip" && row.clientIp != null) return String(row.clientIp);
  return "—";
};

const formatBlockedTime = (row: Record<string, unknown>) => {
  const raw =
    row.timestampEpochMillis != null ? row.timestampEpochMillis
      : row.timestamp != null ? row.timestamp
        : row.blockedAt != null ? row.blockedAt
          : row.createdAt != null ? row.createdAt
            : null;
  if (raw == null) return "—";
  if (typeof raw === "number") return dateFormat(new Date(raw).toISOString()) || String(raw);
  return dateFormat(String(raw)) || String(raw);
};

const blockedRowKey = (row: Record<string, unknown>, idx: number) => {
  if (row.id != null) return String(row.id);
  return "blocked-" + idx;
};

const rowNumber = (idx: number) => (currentPage.value - 1) * pageSize.value + idx + 1;

const viewBlockedDetail = (row: Record<string, unknown>) => {
  selectedBlocked.value = row;
  reasonRevealed.value = false;
  showBlockedDetail.value = true;
};

defineExpose({ refresh: loadBlockedRequests });
</script>

<style scoped>
.blocked-detail .detail-row {
  margin-bottom: 0.5rem;
  font-size: 14px;
}

.blocked-detail .detail-label {
  font-weight: 600;
  margin-right: 0.5rem;
  min-width: 120px;
  display: inline-block;
  vertical-align: top;
}

.blocked-detail .detail-value-wrap {
  word-break: break-all;
  white-space: pre-wrap;
}
</style>
